import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { DisplayColor, NodeDto, WritingDto } from "@mmm/contracts";

export type NodeWithWriting = NodeDto & { writing: WritingDto };
export interface NodeSize {
  width: number;
  height: number;
}

// Bridges the map editor (routed content) and the app sidebar: while a map is
// being edited, the sidebar shows a node-color picker. The editor registers a
// session (current color + pick handler) on mount and clears it on unmount.
export interface MapEditingSession {
  /** The color the picker should highlight — the selected node's color, or the "pen" color for new nodes. */
  color: DisplayColor;
  /** True when a node is selected (picking recolors it); false when picking sets the pen color for new nodes. */
  hasSelection: boolean;
  selectedNode: NodeWithWriting | null;
  onCloseSelection: () => void;
  onPick: (color: DisplayColor) => void;
  onPreviewSize: (size: NodeSize) => void;
  onCommitSize: (size: NodeSize) => void;
}

interface MapEditingContextValue {
  session: MapEditingSession | null;
  setSession: (session: MapEditingSession | null) => void;
}

const MapEditingContext = createContext<MapEditingContextValue | null>(null);

export function MapEditingProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<MapEditingSession | null>(null);
  const value = useMemo(() => ({ session, setSession }), [session]);
  return <MapEditingContext.Provider value={value}>{children}</MapEditingContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMapEditing(): MapEditingContextValue {
  const context = useContext(MapEditingContext);
  if (!context) throw new Error("useMapEditing must be used inside MapEditingProvider");
  return context;
}
